import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Search, FileText, Image as ImageIcon, File as FileIcon, Loader2,
  ExternalLink, Eye, ChevronRight, Building2, Home, ClipboardCheck,
  Filter, X,
} from 'lucide-react';
import AttachmentPreviewModal from '@/components/AttachmentPreviewModal';

/**
 * DocumentsSearchPage — global ranked search across every attachment
 * stored on the system. Searches filename / kind / notes / resolved
 * path. Future Phase D will extend the same backend endpoint to also
 * rank by OCR-extracted text.
 */

const SCOPE_CHIP = {
  appraisal: { label: 'Appraisal', bg: 'bg-orange-100',  text: 'text-orange-800',  ring: 'ring-orange-200',  Icon: ClipboardCheck },
  property:  { label: 'Property',  bg: 'bg-emerald-100', text: 'text-emerald-800', ring: 'ring-emerald-200', Icon: Home },
  client:    { label: 'Client',    bg: 'bg-sky-100',     text: 'text-sky-800',     ring: 'ring-sky-200',     Icon: Building2 },
};

const formatSize = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const iconFor = (mime) => {
  if (!mime) return FileIcon;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf' || mime.startsWith('text/')) return FileText;
  return FileIcon;
};

export default function DocumentsSearchPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [kinds, setKinds] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    api.get('/attachments/kinds').then((r) => setKinds(r.data?.kinds || [])).catch(() => {});
  }, [api]);

  const runSearch = useCallback(async () => {
    if (!q.trim() && !scopeFilter && !kindFilter) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (q.trim()) params.set('q', q.trim());
      if (scopeFilter) params.set('scope', scopeFilter);
      if (kindFilter) params.set('kind', kindFilter);
      const { data } = await api.get(`/attachments/search?${params.toString()}`);
      setResults(data);
    } catch (e) {
      setResults({ items: [], total: 0, error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [api, q, scopeFilter, kindFilter]);

  // Debounced auto-run when q or filters change
  useEffect(() => {
    const t = setTimeout(runSearch, 350);
    return () => clearTimeout(t);
  }, [runSearch]);

  const goToScope = (att) => {
    const ctx = att.context || {};
    if (att.scope === 'appraisal' && ctx.appraisal_id) {
      navigate(`/appraisals/${ctx.appraisal_id}?tab=Attachments`);
    } else if (att.scope === 'property' && ctx.property_id) {
      navigate(`/properties/${ctx.property_id}`);
    } else if (att.scope === 'client' && ctx.client_id) {
      navigate(`/clients/${ctx.client_id}`);
    }
  };

  const clearFilters = () => {
    setQ('');
    setScopeFilter('');
    setKindFilter('');
  };

  return (
    <div className="space-y-6" data-testid="documents-search-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Documents Search</h1>
        <p className="text-sm text-slate-500 mt-1">Search every scan, supplier doc, photo, and form across all Clients, Properties and Appraisals.</p>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="documents-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by filename, kind, notes, or path… (e.g. 'Smith insurance 2024')"
            className="w-full pl-11 pr-10 py-2.5 border border-[#E2E2DF] rounded-md text-sm focus:border-[#D35400] focus:outline-none"
            autoFocus
          />
          {q && (
            <button
              onClick={() => setQ('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Filter size={13} className="text-slate-400" />
          <select
            data-testid="documents-search-scope"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1"
          >
            <option value="">All scopes</option>
            <option value="appraisal">Appraisal</option>
            <option value="property">Property</option>
            <option value="client">Client</option>
          </select>
          <select
            data-testid="documents-search-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1"
          >
            <option value="">All kinds</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          {(q || scopeFilter || kindFilter) && (
            <button
              data-testid="documents-search-clear"
              onClick={clearFilters}
              className="text-slate-500 hover:text-slate-900 underline"
            >
              Clear all
            </button>
          )}
          {results && !loading && (
            <span className="ml-auto text-slate-500">
              {results.total} match{results.total === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Searching…
        </div>
      ) : !results ? (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-6 text-sm text-slate-500 text-center">
          Type something to search, or pick a scope/kind filter above.
        </div>
      ) : results.items.length === 0 ? (
        <div data-testid="documents-search-empty" className="bg-slate-50 border border-slate-200 rounded-md p-6 text-sm text-slate-500 text-center">
          No documents match {q ? `"${q}"` : 'your filters'}.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="documents-search-results">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-2 px-3 w-24">Scope</th>
                <th className="text-left p-2 px-3">Document</th>
                <th className="text-left p-2 px-3 hidden md:table-cell">Context</th>
                <th className="text-right p-2 px-3 w-16 hidden lg:table-cell">Size</th>
                <th className="text-right p-2 px-3 w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.items.map((att) => {
                const Icon = iconFor(att.mime_type);
                const sc = SCOPE_CHIP[att.scope] || SCOPE_CHIP.client;
                const ScopeIcon = sc.Icon;
                const ctx = att.context || {};
                return (
                  <tr key={att._id} data-testid={`documents-search-row-${att._id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="p-2 px-3">
                      <span className={`${sc.bg} ${sc.text} ${sc.ring} ring-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full inline-flex items-center gap-1`}>
                        <ScopeIcon size={10} /> {sc.label}
                      </span>
                    </td>
                    <td className="p-2 px-3">
                      <div className="flex items-start gap-2">
                        <Icon size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{att.original_filename}</div>
                          <div className="text-[10px] text-slate-400">{att.kind}</div>
                          {att.notes && <div className="text-[11px] text-slate-500 italic mt-0.5 truncate max-w-[300px]" title={att.notes}>{att.notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 px-3 hidden md:table-cell text-xs text-slate-600">
                      <div className="flex flex-col gap-0.5">
                        {ctx.client_name && (
                          <button
                            onClick={() => ctx.client_id && navigate(`/clients/${ctx.client_id}`)}
                            className="text-left text-slate-700 hover:text-orange-700 hover:underline inline-flex items-center gap-1"
                          >
                            <Building2 size={10} /> {ctx.client_name}
                          </button>
                        )}
                        {ctx.property_label && (
                          <button
                            onClick={() => ctx.property_id && navigate(`/properties/${ctx.property_id}`)}
                            className="text-left text-slate-600 hover:text-orange-700 hover:underline inline-flex items-center gap-1"
                          >
                            <Home size={10} /> {ctx.property_label}
                          </button>
                        )}
                        {ctx.appraisal_no && (
                          <span className="text-slate-500 inline-flex items-center gap-1">
                            <ClipboardCheck size={10} /> A{ctx.appraisal_no}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 px-3 hidden lg:table-cell text-right text-xs text-slate-500">{formatSize(att.size_bytes)}</td>
                    <td className="p-2 px-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`documents-search-preview-${att._id}`}
                        onClick={() => setPreviewing(att)}
                        className="text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-1 mr-2"
                      >
                        <Eye size={11} /> Preview
                      </button>
                      <button
                        data-testid={`documents-search-jump-${att._id}`}
                        onClick={() => goToScope(att)}
                        className="text-[11px] text-orange-700 hover:underline inline-flex items-center gap-1"
                        title="Jump to the parent record"
                      >
                        <ChevronRight size={11} /> Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {previewing && (
        <AttachmentPreviewModal
          api={api}
          attachment={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}
